import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { memo } from 'react';

import { userLib } from 'entities/user';

import { DeletedUserItem, Pagination } from 'shared/api';

export type DeletedUsersSortBy = 'deletedAt' | 'email';

type Column =
  | { id: DeletedUsersSortBy; label: string; sortable: true; align?: 'left' | 'right' | 'center' }
  | { id: string; label: string; sortable: false; align?: 'left' | 'right' | 'center' };

const columns: Column[] = [
  { id: 'email', label: 'Email', sortable: true, align: 'left' },
  { id: 'source', label: 'Source', sortable: false, align: 'left' },
  { id: 'deletedAt', label: 'Deleted At', sortable: true, align: 'left' },
  { id: 'userId', label: 'User ID', sortable: false, align: 'left' },
];

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type Props = {
  users: DeletedUserItem[] | undefined;
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: DeletedUsersSortBy;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  isError?: boolean;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSortChange: (sortBy: DeletedUsersSortBy) => void;
};

export const DeletedUsersTable = memo(
  ({
    users,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    isError = false,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
  }: Props) => {
    const handleChangePage = (event: unknown, newPage: number) => {
      onPageChange(newPage + 1); // MUI использует 0-based, API использует 1-based
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
            <Table stickyHeader aria-label="deleted users table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortOrder : 'asc'}
                          onClick={() => onSortChange(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isError ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      Failed to load deleted users
                    </TableCell>
                  </TableRow>
                ) : !users ? (
                  // Первая загрузка: данных ещё не было, «не найдены» показывать рано
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user.userId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell align="left">{user.email ?? '—'}</TableCell>
                      <TableCell align="left">
                        <Chip
                          label={userLib.DELETION_SOURCE_META[user.source]?.label ?? user.source}
                          color={userLib.DELETION_SOURCE_META[user.source]?.color ?? 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="left">{formatDateTime(user.deletedAt)}</TableCell>
                      <TableCell align="left">
                        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                          {user.userId}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No deleted users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {pagination && !isError && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            // MUI использует 0-based; прижим к totalPages закрывает рендер между
            // ответом бека и URL-эффектом, когда page из URL ещё за пределами выборки
            page={Math.min(page, Math.max(pagination.totalPages, 1)) - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

DeletedUsersTable.displayName = 'DeletedUsersTable';
